/**
 * Pick a destination city for a contract. Contracts in HST don't carry a
 * geographic destination today — they are abstract jobs. To plot them on the
 * map we deterministically derive a target city from the contract instanceId
 * + the HQ city, biasing toward:
 *   - cities of the same phase or below as the contract's tier
 *   - cities in the HQ's country if the contract is non-EU
 *   - international cities for "Export EU" contracts
 *
 * Determinism: same contract always maps to the same destination across
 * renders (no flicker on every store update).
 */
import { MAP_CITIES, findCityById } from '../data/mapCities';

const hashStr = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

export const deriveContractDestination = (contract, hqCityId, currentPhase) => {
  if (!contract || !hqCityId) return null;
  const hq = findCityById(hqCityId);
  if (!hq) return null;

  const seed = hashStr(contract.instanceId || contract.id || '');
  const isExport = contract.category === 'Export EU';

  // Candidate pool depends on the contract category and the current game phase.
  const pool = MAP_CITIES.filter(c => {
    if (c.id === hqCityId) return false;
    if (c.phase > currentPhase) return false;
    if (isExport) return c.country !== 'FR'; // Export → out of FR
    return c.country === 'FR'; // domestic → French city
  });

  if (pool.length === 0) {
    // Fallback: any non-HQ city within the unlocked phase.
    const fallback = MAP_CITIES.filter(c => c.id !== hqCityId && c.phase <= currentPhase);
    return fallback[seed % Math.max(1, fallback.length)] || null;
  }
  return pool[seed % pool.length];
};
